import { FC, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIndicatorsDialog, { IndicatorValues } from './EditIndicatorsDialog';

interface SoftSkillJunction {
  softSkill: {
    id: number;
    name: string;
  };
  indicators: IndicatorValues[];
}

interface SoftSkillRowProps {
  junction: SoftSkillJunction;
  onRemove: (softSkillId: number) => void;
  onUpdateIndicators: (
    softSkillId: number,
    indicators: IndicatorValues[],
  ) => void;
  removeLoading?: boolean;
  updateLoading?: boolean;
}

const SoftSkillRow: FC<SoftSkillRowProps> = ({
  junction,
  onRemove,
  onUpdateIndicators,
  removeLoading,
  updateLoading,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const indicatorCount = junction.indicators.length;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          px: 1,
        }}
      >
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {junction.softSkill.name}
        </Typography>
        <Chip
          label={`${indicatorCount} indicator${indicatorCount !== 1 ? 's' : ''}`}
          size="small"
          variant="outlined"
        />
        {indicatorCount > 0 && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={() => setEditOpen(true)}
          disabled={updateLoading}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onRemove(junction.softSkill.id)}
          color="error"
          disabled={removeLoading}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 3, pb: 1 }}>
          {junction.indicators.map((ind, i) => (
            <Box
              key={i}
              sx={{
                mb: 1,
                p: 1,
                border: '1px solid #eee',
                borderRadius: 1,
                fontSize: '0.8rem',
              }}
            >
              <Typography variant="caption" fontWeight="bold">
                {ind.indicator}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                {ind.description}
              </Typography>
              {ind.scores.length > 0 && (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Scores: {ind.scores.map(s => `${s.score}=${s.label}`).join(' · ')}
                </Typography>
              )}
              {(ind.negative_evidence.red_flags.length > 0 ||
                ind.negative_evidence.score_reducing_patterns.length > 0) && (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Negative evidence:{' '}
                  {ind.negative_evidence.red_flags.length} red flag(s),{' '}
                  {ind.negative_evidence.score_reducing_patterns.length}{' '}
                  score-reducing pattern(s)
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>

      <EditIndicatorsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        softSkillName={junction.softSkill.name}
        initialIndicators={junction.indicators}
        onSave={indicators => {
          onUpdateIndicators(junction.softSkill.id, indicators);
          setEditOpen(false);
        }}
        loading={updateLoading}
      />
    </>
  );
};

export default SoftSkillRow;
